/**
 * Admin page for managing the conversation signal and strategy catalog.
 *
 * URL: /admin/conversation-signals
 *
 * Features:
 *   - Two tabs: Signals and Strategies.
 *   - Scope filter at the top: Global (default) | stream (searchable dropdown) | user (searchable dropdown).
 *   - Global mode: full CRUD for global signals and strategies (admin only).
 *   - Scoped mode: manage per-scope signal/strategy overrides (owner or admin).
 */

import React, { useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { signalsApi, strategiesApi } from '@/api/conversationSignals';
import { streamApi } from '@/api/stream';
import { chatApi } from '@/api/chat';
import type { ConversationSignal, ConversationStrategy } from '@/types/conversationSignals';
import { SignalModal, StrategyDialog } from '@/components/conversation-signals';
import { useAuth } from '@/hooks/useAuth';

type ScopeMode = 'global' | 'stream' | 'user';

function TabPanel({ children, value, index }: { children: React.ReactNode; value: number; index: number }) {
  return value === index ? <Box sx={{ pt: 3 }}>{children}</Box> : null;
}

// ── Scope filter bar ───────────────────────────────────────────────────────────

interface ScopeOption {
  uuid: string;
  name: string;
}

interface ScopeFilterProps {
  scopeMode: ScopeMode;
  scopeUuid: string;
  onChange: (mode: ScopeMode, uuid: string) => void;
}

function ScopeFilter({ scopeMode, scopeUuid, onChange }: ScopeFilterProps) {
  const { user: currentUser } = useAuth();
  const isAdmin = currentUser?.is_staff || currentUser?.is_superuser;

  // Stream search
  const [streamSearch, setStreamSearch] = useState('');
  const [selectedStream, setSelectedStream] = useState<ScopeOption | null>(null);

  const { data: streamsData, isLoading: isLoadingStreams } = useQuery({
    queryKey: ['scope-streams', streamSearch],
    queryFn: () =>
      streamApi.getStreams({ search: streamSearch || undefined, max_results: 30 }),
    enabled: scopeMode === 'stream',
  });
  const streamOptions: ScopeOption[] = (streamsData?.output?.items ?? []).map((s) => ({
    uuid: s.key,
    name: s.name,
  }));

  // User search
  const [userSearch, setUserSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState<ScopeOption | null>(null);

  const { data: usersData, isLoading: isLoadingUsers } = useQuery({
    queryKey: ['scope-users', userSearch],
    queryFn: () => chatApi.getProjectOwners({ search: userSearch || undefined, max_results: 30 }),
    enabled: scopeMode === 'user' && !!isAdmin,
  });
  const userOptions: ScopeOption[] = (usersData?.items ?? []).map((u) => ({
    uuid: u.uuid,
    name: u.name,
  }));

  const handleModeChange = (mode: ScopeMode) => {
    setSelectedStream(null);
    setSelectedUser(null);
    onChange(mode, '');
  };

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 2,
        alignItems: 'center',
        mb: 3,
        p: 2,
        bgcolor: 'grey.50',
        borderRadius: 2,
        flexWrap: 'wrap',
      }}
    >
      <FormControl size="small" sx={{ minWidth: 140 }}>
        <InputLabel>Scope</InputLabel>
        <Select
          label="Scope"
          value={scopeMode}
          onChange={(e) => handleModeChange(e.target.value as ScopeMode)}
        >
          <MenuItem value="global">Global</MenuItem>
          <MenuItem value="stream">Stream</MenuItem>
          <MenuItem value="user">User</MenuItem>
        </Select>
      </FormControl>

      {scopeMode === 'stream' && (
        <Autocomplete
          size="small"
          sx={{ minWidth: 280 }}
          options={streamOptions}
          getOptionLabel={(o) => o.name}
          isOptionEqualToValue={(o, v) => o.uuid === v.uuid}
          value={selectedStream}
          loading={isLoadingStreams}
          onInputChange={(_, val) => setStreamSearch(val)}
          onChange={(_, val) => {
            setSelectedStream(val);
            onChange('stream', val?.uuid ?? '');
          }}
          renderInput={(params) => (
            <TextField {...params} placeholder="Search streams…" label="Stream" />
          )}
        />
      )}

      {scopeMode === 'user' && (
        <Autocomplete
          size="small"
          sx={{ minWidth: 280 }}
          options={userOptions}
          getOptionLabel={(o) => o.name}
          isOptionEqualToValue={(o, v) => o.uuid === v.uuid}
          value={selectedUser}
          loading={isLoadingUsers}
          onInputChange={(_, val) => setUserSearch(val)}
          onChange={(_, val) => {
            setSelectedUser(val);
            onChange('user', val?.uuid ?? '');
          }}
          renderInput={(params) => (
            <TextField {...params} placeholder="Search users…" label="User" />
          )}
        />
      )}

      {scopeMode !== 'global' && scopeUuid && (
        <Chip
          label={`${scopeMode}: ${
            scopeMode === 'stream'
              ? selectedStream?.name ?? scopeUuid.slice(0, 8) + '…'
              : selectedUser?.name ?? scopeUuid.slice(0, 8) + '…'
          }`}
          color="secondary"
          size="small"
          onDelete={() => {
            setSelectedStream(null);
            setSelectedUser(null);
            onChange(scopeMode, '');
          }}
        />
      )}
    </Box>
  );
}

// ── Signals table ─────────────────────────────────────────────────────────────

interface SignalsTableProps {
  signals: ConversationSignal[];
  loading: boolean;
  isScoped: boolean;
  onEdit: (s: ConversationSignal) => void;
  onDelete: (s: ConversationSignal) => void;
  onToggleActive: (s: ConversationSignal, value: boolean) => void;
  togglingKey: string | null;
}

function SignalsTable({
  signals,
  loading,
  isScoped,
  onEdit,
  onDelete,
  onToggleActive,
  togglingKey,
}: SignalsTableProps) {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  return (
    <TableContainer component={Card} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: 'grey.50' } }}>
            <TableCell>Label</TableCell>
            <TableCell>Category</TableCell>
            <TableCell>Strategies</TableCell>
            {isScoped && <TableCell>Type</TableCell>}
            <TableCell align="center">Active</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {signals.map((s) => (
            <TableRow key={s.key} hover>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      bgcolor: s.filter_colors?.['dot'] ?? '#ccc',
                      flexShrink: 0,
                    }}
                  />
                  <Typography variant="body2" fontWeight={500}>
                    {s.label}
                  </Typography>
                </Box>
              </TableCell>
              <TableCell>
                {s.ui_category ? (
                  <Chip
                    label={s.ui_category}
                    size="small"
                    sx={{
                      fontSize: 11,
                      bgcolor: s.filter_colors?.['bg'] ?? undefined,
                      color: s.filter_colors?.['text'] ?? undefined,
                      border: s.filter_colors?.['border']
                        ? `1px solid ${s.filter_colors['border']}`
                        : undefined,
                    }}
                  />
                ) : (
                  '—'
                )}
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                  {(s.strategies ?? []).map((st, idx) => (
                    <Typography key={st.key} variant="caption" color="text.secondary">
                      {idx + 1}. {st.label}
                    </Typography>
                  ))}
                  {(!s.strategies || s.strategies.length === 0) && (
                    <Typography variant="caption" color="text.disabled">
                      —
                    </Typography>
                  )}
                </Box>
              </TableCell>
              {isScoped && (
                <TableCell>
                  <Chip label="custom" size="small" color="info" sx={{ fontSize: 10 }} />
                </TableCell>
              )}
              <TableCell align="center">
                <Switch
                  size="small"
                  checked={s.is_active}
                  onChange={(e) => onToggleActive(s, e.target.checked)}
                  disabled={togglingKey === s.key}
                />
              </TableCell>
              <TableCell align="right">
                <Tooltip title="Edit">
                  <IconButton size="small" onClick={() => onEdit(s)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton size="small" color="error" onClick={() => onDelete(s)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
          {signals.length === 0 && (
            <TableRow>
              <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                No signals found
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// ── Signals tab ───────────────────────────────────────────────────────────────

interface SignalsTabProps {
  scopeMode: ScopeMode;
  scopeUuid: string;
}

function SignalsTab({ scopeMode, scopeUuid }: SignalsTabProps) {
  const queryClient = useQueryClient();
  const isScoped = scopeMode !== 'global' && !!scopeUuid;

  const [modalOpen, setModalOpen] = useState(false);
  const [editSignal, setEditSignal] = useState<ConversationSignal | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ConversationSignal | null>(null);
  const [togglingKey, setTogglingKey] = useState<string | null>(null);

  const signalsQuery = useQuery({
    queryKey: isScoped
      ? ['conversation-signals', 'scoped', scopeMode, scopeUuid]
      : ['conversation-signals'],
    queryFn: () =>
      isScoped
        ? signalsApi.list({
            scope_type: scopeMode as 'stream' | 'user',
            scope_uuid: scopeUuid,
            max_results: 200,
          })
        : signalsApi.list({ max_results: 200 }),
    enabled: !isScoped || !!scopeUuid,
  });

  const strategiesQuery = useQuery({
    queryKey: isScoped
      ? ['conversation-strategies', 'scoped', scopeMode, scopeUuid, 'with-global']
      : ['conversation-strategies', 'global-only'],
    queryFn: () =>
      isScoped
        ? strategiesApi.list({
            scope_type: scopeMode as 'stream' | 'user',
            scope_uuid: scopeUuid,
            include_global: true,
            max_results: 500,
          })
        : strategiesApi.list({
            only_global: true,
            max_results: 500,
          }),
  });

  const signals: ConversationSignal[] = signalsQuery.data?.results ?? [];
  const allStrategies: ConversationStrategy[] = strategiesQuery.data?.results ?? [];

  const invalidate = () => {
    queryClient.invalidateQueries({
      queryKey: isScoped
        ? ['conversation-signals', 'scoped', scopeMode, scopeUuid]
        : ['conversation-signals'],
    });
  };

  const toggleActiveMutation = useMutation({
    mutationFn: ({ signal, value }: { signal: ConversationSignal; value: boolean }) =>
      signalsApi.update(signal.key, { is_active: value }),
    onMutate: ({ signal }) => setTogglingKey(signal.key),
    onSettled: () => {
      setTogglingKey(null);
      invalidate();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => signalsApi.delete(deleteTarget!.key),
    onSuccess: () => {
      invalidate();
      setDeleteTarget(null);
    },
  });

  const openCreate = () => {
    setEditSignal(null);
    setModalOpen(true);
  };

  const openEdit = (s: ConversationSignal) => {
    setEditSignal(s);
    setModalOpen(true);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" fontWeight={600}>
          Signals ({signals.length})
          {isScoped && (
            <Chip label={`${scopeMode} scope`} size="small" color="secondary" sx={{ ml: 1 }} />
          )}
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          New Signal
        </Button>
      </Box>

      {isScoped && !scopeUuid && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Select a scope above to manage scoped signals.
        </Alert>
      )}

      {isScoped && scopeUuid && signals.length === 0 && !signalsQuery.isLoading && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No custom signals for this {scopeMode}. The global catalog will be used. Add signals
          below to override.
        </Alert>
      )}

      <SignalsTable
        signals={signals}
        loading={signalsQuery.isLoading}
        isScoped={isScoped}
        onEdit={openEdit}
        onDelete={setDeleteTarget}
        onToggleActive={(s, v) => toggleActiveMutation.mutate({ signal: s, value: v })}
        togglingKey={togglingKey}
      />

      <SignalModal
        open={modalOpen}
        signal={editSignal}
        allStrategies={allStrategies}
        scopeType={isScoped ? (scopeMode as 'stream' | 'user') : undefined}
        scopeUuid={isScoped ? scopeUuid : undefined}
        onClose={() => setModalOpen(false)}
        onSaved={() => {
          setModalOpen(false);
          invalidate();
        }}
      />

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Signal</DialogTitle>
        <DialogContent>
          <Typography>
            Delete signal <strong>{deleteTarget?.label}</strong>? This removes it from the AI
            classification catalog.
          </Typography>
          {deleteMutation.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              Failed to delete.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleteMutation.isPending}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => deleteMutation.mutate()}
            disabled={deleteMutation.isPending}
            startIcon={
              deleteMutation.isPending ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <DeleteIcon />
              )
            }
          >
            {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ── Strategies tab ────────────────────────────────────────────────────────────

interface StrategiesTabProps {
  scopeMode: ScopeMode;
  scopeUuid: string;
}

function StrategiesTab({ scopeMode, scopeUuid }: StrategiesTabProps) {
  const queryClient = useQueryClient();
  const isScoped = scopeMode !== 'global' && !!scopeUuid;
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<ConversationStrategy | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ConversationStrategy | null>(null);
  const [search, setSearch] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: isScoped
      ? ['conversation-strategies', 'scoped', scopeMode, scopeUuid]
      : ['conversation-strategies', 'global-only'],
    queryFn: () =>
      isScoped
        ? strategiesApi.list({
            scope_type: scopeMode as 'stream' | 'user',
            scope_uuid: scopeUuid,
            max_results: 500,
          })
        : strategiesApi.list({ only_global: true, max_results: 500 }),
    enabled: !isScoped || !!scopeUuid,
  });

  const strategies: ConversationStrategy[] = (data?.results ?? []).filter(
    (s) => !search || s.label.toLowerCase().includes(search.toLowerCase())
  );

  const invalidate = () => {
    queryClient.invalidateQueries({
      queryKey: isScoped
        ? ['conversation-strategies', 'scoped', scopeMode, scopeUuid]
        : ['conversation-strategies', 'global-only'],
    });
    queryClient.invalidateQueries({ queryKey: ['conversation-signals'] });
  };

  const deleteMutation = useMutation({
    mutationFn: () => strategiesApi.delete(deleteTarget!.key),
    onSuccess: () => {
      invalidate();
      setDeleteTarget(null);
    },
  });

  const openCreate = () => {
    setEditTarget(null);
    setDialogOpen(true);
  };
  const openEdit = (s: ConversationStrategy) => {
    setEditTarget(s);
    setDialogOpen(true);
  };

  return (
    <Box>
      <Box
        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
          <Typography variant="h6" fontWeight={600}>
            Strategies ({strategies.length})
            {isScoped && (
              <Chip label={`${scopeMode} scope`} size="small" color="secondary" sx={{ ml: 1 }} />
            )}
          </Typography>
          <TextField
            size="small"
            placeholder="Search…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ maxWidth: 260 }}
          />
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          Add Strategy
        </Button>
      </Box>

      {isScoped && !scopeUuid && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Select a scope above to manage scoped strategies.
        </Alert>
      )}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Card} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: 'grey.50' } }}>
                <TableCell>Label</TableCell>
                <TableCell>Description</TableCell>
                {isScoped && <TableCell>Scope</TableCell>}
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {strategies.map((s) => (
                <TableRow key={s.key} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {s.label}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 480 }}>
                      {s.description ?? '—'}
                    </Typography>
                  </TableCell>
                  {isScoped && (
                    <TableCell>
                      {s.creator_uuid || s.stream_uuid ? (
                        <Chip label="custom" size="small" color="info" sx={{ fontSize: 10 }} />
                      ) : (
                        <Chip label="global" size="small" variant="outlined" sx={{ fontSize: 10 }} />
                      )}
                    </TableCell>
                  )}
                  <TableCell align="right">
                    {/* Only show edit/delete for custom (non-global) strategies when in scoped mode */}
                    {(!isScoped || s.creator_uuid || s.stream_uuid) && (
                      <>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => openEdit(s)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={() => setDeleteTarget(s)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {strategies.length === 0 && (
                <TableRow>
                  <TableCell colSpan={isScoped ? 4 : 3} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    No strategies found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <StrategyDialog
        open={dialogOpen}
        strategy={editTarget}
        scopeType={isScoped ? (scopeMode as 'stream' | 'user') : undefined}
        scopeUuid={isScoped ? scopeUuid : undefined}
        onClose={() => setDialogOpen(false)}
        onSaved={() => {
          setDialogOpen(false);
          invalidate();
        }}
      />

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Strategy</DialogTitle>
        <DialogContent>
          <Typography>
            Delete <strong>{deleteTarget?.label}</strong>? This will also remove it from any
            signal strategy lists.
          </Typography>
          {deleteMutation.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              Failed to delete.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleteMutation.isPending}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => deleteMutation.mutate()}
            disabled={deleteMutation.isPending}
            startIcon={
              deleteMutation.isPending ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <DeleteIcon />
              )
            }
          >
            {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export const ConversationSignals: React.FC = () => {
  const [tab, setTab] = useState(0);
  const [scopeMode, setScopeMode] = useState<ScopeMode>('global');
  const [scopeUuid, setScopeUuid] = useState('');

  const handleScopeChange = (mode: ScopeMode, uuid: string) => {
    setScopeMode(mode);
    setScopeUuid(uuid);
  };

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" fontWeight={700} mb={1}>
          Conversation Signal Catalog
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage AI-detectable conversation signals, their strategy matrices, and UI metadata.
          Changes take effect immediately — the catalog cache is refreshed on every save.
        </Typography>
      </Box>

      <ScopeFilter scopeMode={scopeMode} scopeUuid={scopeUuid} onChange={handleScopeChange} />

      <Card>
        <CardContent sx={{ p: 0 }}>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}
          >
            <Tab label="Signals" />
            <Tab label="Strategies" />
          </Tabs>
          <Box sx={{ p: 3 }}>
            <TabPanel value={tab} index={0}>
              <SignalsTab scopeMode={scopeMode} scopeUuid={scopeUuid} />
            </TabPanel>
            <TabPanel value={tab} index={1}>
              <StrategiesTab scopeMode={scopeMode} scopeUuid={scopeUuid} />
            </TabPanel>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ConversationSignals;
